export const PUBLIC_FORM_URL_FALLBACK_ORIGIN = 'https://miao-form.case4.app'

export const UUID_FORM_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

type SearchParamValue = string | number | boolean | null | undefined

type PublicFormUrlOptions = {
  origin?: string | null
  fallbackOrigin?: string
  searchParams?: URLSearchParams | Record<string, SearchParamValue>
}

export function isUuidFormId(value: string): boolean {
  return UUID_FORM_ID_PATTERN.test(value)
}

export function getCurrentBrowserOrigin(fallbackOrigin = PUBLIC_FORM_URL_FALLBACK_ORIGIN): string {
  if (typeof window === 'undefined') return fallbackOrigin
  const origin = window.location?.origin
  if (!origin || origin === 'null') return fallbackOrigin
  return origin
}

export function buildPublicFormPath(formId: string): string {
  return `/to/${encodeURIComponent(formId)}`
}

export function buildCurrentOriginUrl(
  path: string,
  options: Pick<PublicFormUrlOptions, 'fallbackOrigin' | 'origin'> = {},
): string {
  const origin =
    options.origin && options.origin !== 'null'
      ? options.origin
      : getCurrentBrowserOrigin(options.fallbackOrigin)
  return new URL(path, `${origin}/`).toString()
}

export function buildPublicFormUrl(formId: string, options: PublicFormUrlOptions = {}): string {
  const origin =
    options.origin && options.origin !== 'null'
      ? options.origin
      : getCurrentBrowserOrigin(options.fallbackOrigin)
  const url = new URL(buildPublicFormPath(formId), `${origin}/`)

  if (options.searchParams instanceof URLSearchParams) {
    options.searchParams.forEach((value, key) => url.searchParams.set(key, value))
  } else if (options.searchParams) {
    for (const [key, value] of Object.entries(options.searchParams)) {
      if (value === undefined || value === null || value === '') continue
      url.searchParams.set(key, String(value))
    }
  }

  return url.toString()
}

export function rebasePublicFormUrl(
  sourceUrl: string | null | undefined,
  formId: string,
  options: PublicFormUrlOptions = {},
): string {
  const rebased = new URL(buildPublicFormUrl(formId, options))
  if (!sourceUrl) return rebased.toString()

  try {
    const source = new URL(sourceUrl, rebased.origin)
    source.searchParams.forEach((value, key) => rebased.searchParams.set(key, value))
  } catch {
    // Malformed persisted mock URLs are ignored so the visible share URL stays usable.
  }

  return rebased.toString()
}
